import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  ApplicationInsights,
  IEventTelemetry,
  IExceptionTelemetry,
  IMetricTelemetry,
  IPageViewTelemetry,
  ITelemetryItem,
  ITraceTelemetry,
  Snippet,
} from '@microsoft/applicationinsights-web';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { environment } from '#environments';
import { TelemetryEvent } from '#enums';

const CLOUD_ROLE_NAME = 'iopoc-web';

// Custom events carry identifiers, codes, and counts only: never free text, names, comments, search terms, or evidence.
const TELEMETRY_PROPERTY_KEYS = [
  'engineCaseId',
  'caseType',
  'classId',
  'classVersion',
  'stepId',
  'outcome',
  'status',
  'command',
  'useCase',
  'workflowId',
  'agentRunId',
  'filterKeys',
  'thumb',
  'reasonCount',
] as const;

export type TelemetryEventProperties = Partial<Record<(typeof TELEMETRY_PROPERTY_KEYS)[number], string | number | null>>;

const TELEMETRY_PROPERTY_ALLOWLIST: ReadonlySet<string> = new Set(TELEMETRY_PROPERTY_KEYS);

/** The HTTP status of a failed request, or 0 when no HTTP response arrived. */
export function httpStatusOf(error: unknown): number {
  return error instanceof HttpErrorResponse ? error.status : 0;
}

// Query strings and fragments can carry free text (for example asset names in searches), so these fields keep only the path.
const URL_FIELDS_BY_BASE_TYPE: Record<string, string[]> = {
  PageviewData: ['uri', 'refUri'],
  PageviewPerformanceData: ['uri'],
  RemoteDependencyData: ['name', 'target', 'data'],
};

// A signal key comes from detection data and can name a well or asset, so it never leaves the browser in a URL.
const SIGNAL_KEY_SEGMENT = /\/signals\/[^/?#]+/g;

@Injectable({
  providedIn: 'root',
})
export class TelemetryService {
  private _router = inject(Router);
  private _applicationInsights: ApplicationInsights | null = null;

  initialize(): void {
    // Deployed bundles ship a placeholder that the deploy pipeline replaces; anything other than a real connection string keeps telemetry off.
    if (this._applicationInsights || !environment.appInsightsConnectionString.includes('InstrumentationKey=')) {
      return;
    }

    // The app initializer calls this before authentication starts, so a setup failure must leave telemetry off, never stop the app.
    let sdk: ApplicationInsights | null = null;
    try {
      const angularPlugin = new AngularPlugin();
      sdk = this.createSdk({
        config: {
          connectionString: environment.appInsightsConnectionString,
          enableAutoRouteTracking: true,
          // Browsers block the deprecated unload event by permissions policy; the SDK still flushes on pagehide and visibilitychange.
          disablePageUnloadEvents: ['unload'],
          enableCorsCorrelation: true,
          correlationHeaderDomains: correlationHeaderDomains(),
          extensions: [angularPlugin],
          extensionConfig: {
            [angularPlugin.identifier]: {
              router: this._router,
            },
          },
        },
      });
      sdk.addTelemetryInitializer(enrichTelemetry);
      sdk.loadAppInsights();
      this._applicationInsights = sdk;
    } catch {
      this._applicationInsights = null;
      this._stopQuietly(sdk);
    }
  }

  setAuthenticatedUser(userId: string): void {
    if (!userId) {
      return;
    }

    this._send(sdk => sdk.setAuthenticatedUserContext(userId));
  }

  clearAuthenticatedUser(): void {
    this._send(sdk => sdk.clearAuthenticatedUserContext());
  }

  trackException(exception: IExceptionTelemetry): void {
    this._send(sdk => sdk.trackException(exception));
  }

  trackPageView(pageView?: IPageViewTelemetry): void {
    this._send(sdk => sdk.trackPageView(pageView));
  }

  trackTrace(trace: ITraceTelemetry): void {
    this._send(sdk => sdk.trackTrace(trace));
  }

  trackMetric(metric: IMetricTelemetry): void {
    this._send(sdk => sdk.trackMetric(metric));
  }

  trackEvent(event: IEventTelemetry): void {
    this._send(sdk => sdk.trackEvent(event));
  }

  /** Tracks a named application event, dropping any property outside the allowlist and any empty value. */
  trackAppEvent(name: TelemetryEvent, properties: TelemetryEventProperties = {}): void {
    const allowed: Record<string, string> = {};
    for (const [key, value] of Object.entries(properties)) {
      if (TELEMETRY_PROPERTY_ALLOWLIST.has(key) && value !== undefined && value !== null && value !== '') {
        allowed[key] = String(value);
      }
    }
    this.trackEvent({ name, properties: allowed });
  }

  /** Tracks a failed command with its HTTP status, or 0 when no HTTP response arrived. */
  trackCommandFailed(command: string, error: unknown, properties: TelemetryEventProperties = {}): void {
    this.trackAppEvent(TelemetryEvent.CaseCommandFailed, { ...properties, command, status: httpStatusOf(error) });
  }

  // Separate from initialize() so tests can check the SDK configuration without loading the real SDK.
  protected createSdk(snippet: Snippet): ApplicationInsights {
    return new ApplicationInsights(snippet);
  }

  private _send(action: (sdk: ApplicationInsights) => void): void {
    if (!this._applicationInsights) {
      return;
    }

    try {
      action(this._applicationInsights);
    } catch {
      // Telemetry must never break a user flow, the error handler, or app start-up.
    }
  }

  // Stop a partially initialized SDK before allowing a later initialization attempt.
  private _stopQuietly(sdk: ApplicationInsights | null): void {
    try {
      sdk?.unload(false);
    } catch {
      // Nothing more can be done; the SDK reference is already cleared.
    }
  }
}

/** Hosts that receive trace headers so browser calls join the API's traces; the engine joins only when its CORS policy allows them. */
function correlationHeaderDomains(): string[] {
  const urls = [environment.apiUrl, environment.appInsightsCorrelateEngine ? environment.engineApiUrl : undefined];
  return urls.filter((url): url is string => !!url).map(url => new URL(url).host);
}

function enrichTelemetry(item: ITelemetryItem): void {
  item.tags ??= {};
  item.tags['ai.cloud.role'] = CLOUD_ROLE_NAME;

  const baseData = item.baseData;
  const fields = URL_FIELDS_BY_BASE_TYPE[item.baseType ?? ''];
  if (!baseData || !fields) {
    return;
  }

  for (const field of fields) {
    const value = baseData[field];
    if (typeof value === 'string') {
      baseData[field] = sanitizeUrl(value);
    }
  }
}

// Drops the query string and fragment, and redacts the signal key that `DossierService.resolveSuggestion` puts in the path.
function sanitizeUrl(value: string): string {
  return value.split(/[?#]/, 1)[0].replace(SIGNAL_KEY_SEGMENT, '/signals/{signalKey}');
}